/* eslint-disable max-lines-per-function */
import React, { useEffect } from 'react';
import Modal from '+/Modal';
import { displayMoneyString } from '../../../../utils/money';
import dayjs from 'dayjs';
import { actions as paymentActionsSeatBased } from '../../../payment/seat-based/sagaSlice';
import { useSelector, useDispatch } from 'react-redux';
import { Invoice, InvoiceLineItem } from '~/components/payment/seat-based/types/types';
import { products } from '~/components/payment/seat-based/constants';

interface Props {
  dto: Invoice | null;
  showModal: boolean;
  setShowModal: React.Dispatch<React.SetStateAction<boolean>>;
}

const getTableRow = (line: $TSFixMe, index: number, printPeriodHeader: boolean) => (
  <>
    {printPeriodHeader && line.period && (
      <tr>
        <td colSpan={2}>
          <div className='period-dates'>
            <hr />
            {dayjs.unix(line.period.start).format('MMMM D, YYYY')} -{' '}
            {dayjs.unix(line.period.end).format('MMMM D, YYYY')}
            <hr />
          </div>
        </td>
      </tr>
    )}
    <tr key={index}>
      <td>{line.description}</td>
      <td className='money-cell'>{formatMoney(line.amount)}</td>
    </tr>
  </>
);

const formatMoney = (cents: number | undefined): string => {
  if (!cents) {
    return displayMoneyString(0);
  }
  if (cents < 0) {
    // Reformat to put negative sign to left of dollar sign.
    const tempPositiveCents = cents * -1;
    const tempResult = displayMoneyString(tempPositiveCents / 100);

    return `-${tempResult}`;
  }

  return displayMoneyString(cents / 100);
};

const productArray = Object.entries(products).map(([_, value]) => value);

const getTaxCodeFromLineItem = (line: InvoiceLineItem): string => {
  const lookupKey = line.price.lookup_key;
  const matchingProduct = productArray.find((product) => product.LOOKUP_KEY === lookupKey);
  if (!matchingProduct) return '';
  return matchingProduct.TAX_CATEGORY;
};

export default ({ dto, showModal, setShowModal }: Props) => {
  const dispatch = useDispatch();
  const { taxData } = useSelector<State, PaymentSeatBasedState>((state) => state.paymentSeatBased);
  const { customer } = useSelector<State, MainState>((state) => state.main);
  const addressPostalCode = customer && customer.length ? customer[0].address_postal_code : '';
  const taxTotal = taxData && taxData.totalTax ? taxData.totalTax * 100 : 0;
  useEffect(() => {
    if (!dto || !addressPostalCode) {
      dispatch(paymentActionsSeatBased.resetTaxData());
      return;
    }
    const dtoLineData = dto.lines.data.map((line: InvoiceLineItem) => {
      const discountAmount = line.discount_amounts.reduce(
        (acc: number, discount: { amount: number; discount: string }) => acc + discount.amount,
        0
      );
      return {
        quantity: line.quantity,
        amount: (line.amount - discountAmount) / 100,
        taxCode: getTaxCodeFromLineItem(line),
      };
    });
    const taxableLineItems = dtoLineData.filter((lineItem) => lineItem.taxCode !== '');
    if (showModal) {
      const payload = {
        lineItems: taxableLineItems,
        postalCode: addressPostalCode,
      };
      // @ts-ignore
      dispatch(paymentActionsSeatBased.getSalesTax(payload));
    }
  }, [addressPostalCode, dispatch, dto, showModal]);

  if (!dto) {
    return null;
  }

  const getDiscountName = (discountId: string) => {
    if (dto.discount && dto.discount.id === discountId && dto.discount.coupon) {
      return dto.discount.coupon.name;
    }

    // Unknown discount name
    return '';
  };

  let lastPeriodStart: number = 0;
  let lastPeriodEnd: number = 0;
  let printPeriodHeader = false;
  return (
    <>
      {dto && (
        <Modal
          additionalCssClasses='upcoming-invoice-modal'
          visible={showModal}
          closeModal={() => setShowModal(false)}
        >
          <>
            <h3>Upcoming Invoice</h3>
            <h5>Summary</h5>
            <table>
              <tr>
                <td className='label-cell'>Payment Date</td>
                <td>{dayjs.unix(dto.period_end).format('MMMM D, YYYY')}</td>
              </tr>
              <tr>
                <td className='label-cell'>To</td>
                <td>{dto.customer_name}</td>
              </tr>
              <tr>
                <td className='label-cell'>From</td>
                <td>{dto.account_name}</td>
              </tr>
            </table>
            <hr />
            <h5>Items</h5>
            <table className='items-table'>
              {dto.lines.data && dto.lines.data[0] && dto.lines.data[0].period && (
                <tr>
                  <td colSpan={2}>
                    <div className='period-dates'>
                      <hr />
                      {dayjs.unix(dto.lines.data[0].period.start).format('MMMM D, YYYY')} -{' '}
                      {dayjs.unix(dto.lines.data[0].period.end).format('MMMM D, YYYY')}
                      <hr />
                    </div>
                  </td>
                </tr>
              )}
              {dto.lines.data.map((line: $TSFixMe, index: number) => {
                if (lastPeriodStart === 0) {
                  lastPeriodStart = dto.lines.data[0].period.start;
                  lastPeriodEnd = dto.lines.data[0].period.end;
                }
                if (
                  dayjs.unix(lastPeriodStart).format('MMMM D, YYYY') !==
                    dayjs.unix(dto.lines.data[index].period.start).format('MMMM D, YYYY') ||
                  dayjs.unix(lastPeriodEnd).format('MMMM D, YYYY') !==
                    dayjs.unix(dto.lines.data[index].period.end).format('MMMM D, YYYY')
                ) {
                  printPeriodHeader = true;
                  lastPeriodStart = dto.lines.data[index].period.start;
                  lastPeriodEnd = dto.lines.data[index].period.end;
                } else {
                  printPeriodHeader = false;
                }
                return getTableRow(line, index, printPeriodHeader);
              })}
              <tr>
                <td>
                  <span className='semi-bold'>Subtotal</span>
                </td>
                <td className='money-cell'>{formatMoney(dto.subtotal)}</td>
              </tr>

              {dto.total_discount_amounts && dto.total_discount_amounts.length > 0 && (
                <tr>
                  <td colSpan={2}>
                    <hr />
                  </td>
                </tr>
              )}

              {dto.total_discount_amounts.map(
                (discountAmountItem: $TSFixMe, discountAmountIndex: number) => (
                  <tr className='discount-row' key={discountAmountIndex}>
                    <td>{getDiscountName(discountAmountItem.discount)}</td>

                    <td className='money-cell'>-{formatMoney(discountAmountItem.amount)}</td>
                  </tr>
                )
              )}

              {dto.starting_balance && dto.starting_balance < 0 ? (
                <>
                  <tr>
                    <td colSpan={2}>
                      <hr />
                    </td>
                  </tr>
                  <tr>
                    <td>Account Credit</td>

                    <td className='money-cell'>{formatMoney(dto.starting_balance)}</td>
                  </tr>
                </>
              ) : (
                ''
              )}

              {dto.starting_balance && dto.starting_balance > 0 ? (
                <>
                  <tr>
                    <td colSpan={2}>
                      <hr />
                    </td>
                  </tr>
                  <tr>
                    <td>Account Balance</td>

                    <td className='money-cell'>{formatMoney(dto.starting_balance)}</td>
                  </tr>
                </>
              ) : (
                ''
              )}

              {taxData ? (
                <tr>
                  <td>
                    <span className='italic'>Sales Tax</span>
                  </td>
                  <td className='money-cell'>{formatMoney(taxTotal)}</td>
                </tr>
              ) : null}

              <tr>
                <td colSpan={2}>
                  <hr />
                </td>
              </tr>

              <tr>
                <td>
                  <span className='semi-bold'>Amount due</span>
                </td>
                <td className='money-cell'>{formatMoney(dto.amount_due + taxTotal)}</td>
              </tr>
            </table>
          </>
        </Modal>
      )}
    </>
  );
};
